import { useEffect, useState } from 'react'
import {
  api,
  type CapsuleOptions,
  type ExcipientRequirementOption,
  type Product as ProductDetail,
  type ProductTerm,
} from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { EntityForm, Field, FieldGroup } from '../components/EntityForm.tsx'
import { Tag } from '../components/Tag.tsx'
import { Input } from '../components/Input.tsx'
import { useToast } from '../components/Toast.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { ExcipientIndication } from '../components/ExcipientIndication.tsx'
import { ExcipientOverride } from '../components/ExcipientOverride.tsx'
import { ShellCorrection } from '../components/ShellCorrection.tsx'
import { icons } from '../components/icons.ts'

/**
 * One product in our copy of the pharmacy's catalogue, and every term it can
 * be found by.
 *
 * This is where synonym candidates should be judged, rather than in the global
 * queue. That queue shows "Vitamina B12 → P-1234, proposed by model" with
 * nothing to compare it against; here the candidate sits beside the terms
 * already confirmed for the product, which is the context somebody needs to
 * answer the only question that matters -- whether these are the same
 * substance, or merely two things this pharmacy swaps for one another.
 *
 * The distinction is the whole safety story. An synonym makes a class of finding
 * stop being raised, permanently, for everybody. A substitution preference
 * recorded as an synonym is how the verification quietly stops verifying.
 */

const SOURCE_LABEL: Readonly<Record<ProductTerm['source'], string>> = {
  erp: 'Do ERP',
  pharmacist: 'Quave Magistral',
  model: 'Sugerido',
}

export function Product({
  erpCode,
  accountId,
  onBack,
}: {
  erpCode: string
  /** The session's account, whose catalogue this is: excipient answers and shells are set on it. */
  accountId: string | undefined
  onBack: () => void
}) {
  const [product, setProduct] = useState<ProductDetail | undefined>()
  const [requirements, setRequirements] = useState<ExcipientRequirementOption[]>([])
  const [capsule, setCapsule] = useState<CapsuleOptions>({ axes: [], traits: [] })
  const [busy, setBusy] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()
  const confirm = useConfirm()

  async function load() {
    try {
      const found = await api.product(erpCode)
      setProduct(found.product)
      setRequirements(found.excipientRequirements)
      setCapsule(found.capsule)
      setError(undefined)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao carregar o produto')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erpCode])

  async function act(id: string, action: 'approve' | 'reject' | 'remove') {
    if (action === 'remove') {
      const term = product?.terms.find((t) => t.id === id)
      const sure = await confirm({
        title: `Remover "${term?.term ?? 'este sinônimo'}"?`,
        message:
          'A receita deixa de encontrar este produto por esse nome nas próximas conferências, e o sistema não volta a propor o mesmo termo.',
        confirmLabel: 'Remover',
        tone: 'danger',
      })
      if (!sure) return
    }
    setBusy(id)
    try {
      const term = product?.terms.find((t) => t.id === id)?.term ?? 'sinônimo'
      if (action === 'approve') await api.approveSynonym(id)
      else if (action === 'remove') await api.removeTerm(id)
      else await api.rejectSynonym(id)
      await load()
      toast.show(
        action === 'approve'
          ? `"${term}" aprovado`
          : action === 'remove'
            ? `"${term}" removido`
            : `"${term}" descartado`,
        { tone: 'success' },
      )
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao registrar', { tone: 'error' })
    } finally {
      setBusy(undefined)
    }
  }

  if (error !== undefined && product === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-5">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Voltar
        </Button>
        <h1 className="text-tH4 text-text-strong">Produto não encontrado</h1>
        <p className="text-tP text-text-weak">{error}</p>
      </div>
    )
  }

  if (product === undefined) {
    return (
      <p className="text-tP text-text-weak p-5" role="status" aria-live="polite">
        Carregando…
      </p>
    )
  }

  const pending = product.terms.filter((t) => !t.approved)
  const confirmed = product.terms.filter((t) => t.approved)

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Voltar
        </Button>
        <h1 className="text-tH4 text-text-strong">{product.name}</h1>
        <span className="text-tSmall text-text-weak">{product.erpCode}</span>
        {/*
          Retired, not missing. Products are deactivated rather than deleted
          because verifications from months ago cite them by id, and answering "not
          found" would turn a historical finding into a dead link.
        */}
        {!product.active && (
          <span className="text-tSmall text-text-warning">
            Produto desativado no ERP — mantido por causa de conferências anteriores
          </span>
        )}
      </div>

      {/*
        Every catalogue field, as a form nobody can edit -- yet. Shown whole,
        dashes and all, rather than only the fields the ERP filled: a product
        page that changes shape from product to product reads as broken, and
        "this is on no list" is itself an answer somebody came here for.
      */}
      <EntityForm description="O que o catálogo da farmácia diz sobre este produto. Nada aqui se edita; os termos que o encontram ficam abaixo.">
        <Field label="Código" value={product.erpCode} />
        <Field label="Nome" value={product.name} />
        <Field label="Situação" value={product.active ? 'Ativo' : 'Desativado no ERP'} />
        <Field
          label="Sincronizado"
          value={
            product.syncedAt === null
              ? 'nunca'
              : new Date(product.syncedAt).toLocaleString('pt-BR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })
          }
        />
        <Field label="Lista de controle" value={product.controlledList ?? '—'} />
        <Field
          label="SNGPC"
          value={product.reportedToSngpc === null ? '—' : product.reportedToSngpc ? 'Sim' : 'Não'}
        />
        <Field
          label="Classe biofarmacêutica"
          value={product.biopharmaceuticalClass?.toUpperCase() ?? '—'}
        />
        <Field
          label="Higroscopicidade"
          value={product.hygroscopicity === null ? '—' : String(product.hygroscopicity)}
        />
        <FieldGroup
          label="Excipiente indicado para"
          helperText="Para que tipo de ativo este produto é o excipiente certo nesta farmácia. A regra “Checar excipiente” compara com a classe biofarmacêutica e a higroscopicidade dos ativos do pote; um produto sem nenhuma marcação nunca é avaliado. Vale a partir da próxima conferência."
        >
          <ExcipientIndication
            accountId={accountId}
            product={product}
            requirements={requirements}
            onChanged={() => void load()}
          />
        </FieldGroup>
        <FieldGroup
          label="Invólucro de cápsula"
          helperText="O que este produto é quando puxado como o invólucro da cápsula: material, cor e liberação. A conferência compara com o que a receita pede. Um produto cujo nome começa com CAP ou CAPSULA é lido pelo nome; corrija aqui quando o nome engana, ou marque como invólucro um produto com outro nome. Vale a partir da próxima conferência."
        >
          <ShellCorrection
            accountId={accountId}
            product={product}
            options={capsule}
            onChanged={() => void load()}
          />
        </FieldGroup>
        <FieldGroup
          label="Tipo na comparação"
          helperText="O ERP diz, em cada orçamento, se uma linha é componente ou excipiente. Quando ele registra como componente algo que é excipiente, esta farmácia pode tratá-lo como excipiente: deixa de ser comparado com a receita nas próximas conferências."
        >
          <ExcipientOverride product={product} onChanged={() => void load()} />
        </FieldGroup>
        <FieldGroup
          label="Sinônimos"
          helperText="Os termos que encontram este produto numa receita. Um sinônimo aprovado passa a valer na próxima conferência, para todas as receitas desta farmácia; aprove apenas se for a mesma substância. Descartar tira da lista sem apagar o registro."
        >
          <DataTable
            columns={termColumns(busy, act)}
            rows={[...pending, ...confirmed]}
            rowKey={(t) => t.id}
            footer={<AddTerm erpCode={product.erpCode} onAdded={() => void load()} />}
          />
        </FieldGroup>
      </EntityForm>
    </div>
  )
}

function termColumns(
  busy: string | undefined,
  act: (id: string, action: 'approve' | 'reject' | 'remove') => void,
): ReadonlyArray<Column<ProductTerm>> {
  return [
    {
      key: 'term',
      header: 'Termo',
      render: (t) => <span className={t.approved ? '' : 'font-semibold'}>{t.term}</span>,
    },
    {
      key: 'source',
      header: 'Origem',
      className: 'text-text-weak',
      render: (t) => (t.kind === 'canonical' ? 'Nome no catálogo' : SOURCE_LABEL[t.source]),
    },
    {
      key: 'status',
      header: 'Situação',
      render: (t) =>
        t.approved ? (
          <Tag tone="success">Aprovado</Tag>
        ) : (
          <Tag tone="warning">Aguardando aprovação</Tag>
        ),
    },
    {
      key: 'occurrences',
      header: 'Vezes proposto',
      className: 'text-text-weak tabular-nums',
      // How many readings proposed this same term: the case for approving it.
      render: (t) => (t.occurrences > 1 ? `${t.occurrences}×` : '—'),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-px whitespace-nowrap text-right',
      headerClassName: 'w-px',
      render: (t) =>
        t.approved ? (
          // Ours to take back; the catalogue's own name and the ERP's synonyms
          // come back on the next sync, so they carry no button.
          t.kind === 'synonym' && t.source !== 'erp' ? (
            <Button
              size="small"
              variant="secondary"
              tone="danger"
              aria-label={`Remover "${t.term}"`}
              title="Remover"
              leadingIcon={icons.remove}
              disabled={busy === t.id}
              onClick={() => act(t.id, 'remove')}
            />
          ) : null
        ) : (
          <span className="flex justify-end gap-2">
            <Button
              size="small"
              variant="secondary"
              tone="brand"
              disabled={busy === t.id}
              onClick={() => act(t.id, 'approve')}
            >
              Aprovar
            </Button>
            <Button
              size="small"
              variant="secondary"
              tone="neutral"
              disabled={busy === t.id}
              onClick={() => act(t.id, 'reject')}
            >
              Descartar
            </Button>
          </span>
        ),
    },
  ]
}

/**
 * A synonym typed straight into the table's last row.
 *
 * Enter or the button adds it, approved: the person typing it has already
 * decided. The table reloads underneath and the box clears for the next.
 */
function AddTerm({ erpCode, onAdded }: { erpCode: string; onAdded: () => void }) {
  const [term, setTerm] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const ready = term.trim() !== ''

  async function add() {
    if (!ready) return
    setBusy(true)
    try {
      await api.addTerm(erpCode, term.trim())
      toast.show(`"${term.trim()}" adicionado`, { tone: 'success' })
      setTerm('')
      onAdded()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao adicionar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  // Not a <form>: this row lives inside the entity form, and a form inside a
  // form is invalid HTML. Enter on the box does what the button does.
  return (
    <div className="flex items-center gap-2">
      <Input
        size="small"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          void add()
        }}
        placeholder="Adicionar sinônimo…"
        aria-label="Novo sinônimo"
        containerClassName="max-w-[24rem]"
        disabled={busy}
      />
      <Button
        type="button"
        size="small"
        variant="secondary"
        tone="brand"
        disabled={busy || !ready}
        onClick={() => void add()}
      >
        {busy ? 'Adicionando…' : 'Adicionar'}
      </Button>
    </div>
  )
}
